import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUpRight, RotateCcw, CheckCircle2, XCircle } from "lucide-react";
import { RevealLine, FadeUp } from "../components/Reveal";
import { CALENDLY_URL, HEALTH_QUESTIONS } from "../data/content";

const grade = (score) => {
  if (score >= 9) return { label: "Excellent", color: "text-green", note: "Your finance function is in rare shape. The next step is using it offensively — pricing, hiring, capital." };
  if (score >= 7) return { label: "Good", color: "text-green", note: "Solid foundations with a few gaps. Fix the misses below before they become expensive." };
  if (score >= 4) return { label: "Needs work", color: "text-amber", note: "You're flying with partial instruments. The gaps below are costing you money and sleep — in that order." };
  return { label: "Critical", color: "text-amber", note: "Your business is running on instinct, not numbers. This is fixable — and faster than you think." };
};

export default function HealthCheck() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const done = answers.length === HEALTH_QUESTIONS.length;
  const score = answers.filter(Boolean).length;
  const g = grade(score);

  const answer = (val) => {
    setAnswers([...answers, val]);
    setStep(step + 1);
  };

  const reset = () => { setStep(0); setAnswers([]); };

  return (
    <main className="pt-32 pb-16" data-testid="health-check-page">
      <div className="max-w-3xl mx-auto px-6">
        <FadeUp y={12}>
          <Link to="/#resources" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-soft hover:text-green transition-colors duration-200 mb-10" data-testid="tool-back-link">
            <ArrowLeft size={13} /> All resources
          </Link>
        </FadeUp>
        <p className="font-mono text-lg uppercase tracking-[0.25em] text-ink-mid mb-5">Interactive self-assessment · 5 minutes</p>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-ink mb-6">
          <RevealLine animateOnLoad>Finance Function <span className="italic text-green">Health Check.</span></RevealLine>
        </h1>
        <FadeUp className="text-ink-mid leading-relaxed mb-14">
          10 questions every founder should be able to answer about their business. Instant score, question-by-question breakdown, and exactly what to fix first.
        </FadeUp>

        {!done ? (
          <FadeUp>
            <div className="bg-white rounded-2xl p-8 lg:p-12" data-testid="health-quiz">
              <div className="flex items-center justify-between mb-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-soft">Question {step + 1} of {HEALTH_QUESTIONS.length}</p>
                <div className="flex gap-1">
                  {HEALTH_QUESTIONS.map((_, i) => (
                    <span key={i} className={`h-1 w-5 rounded-full transition-colors duration-300 ${i < step ? "bg-green" : i === step ? "bg-green/40" : "bg-rule"}`} />
                  ))}
                </div>
              </div>
              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                  <h2 className="font-serif text-2xl lg:text-3xl text-ink leading-snug mb-10" data-testid="health-question">{HEALTH_QUESTIONS[step].q}</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <button onClick={() => answer(true)} data-testid="health-yes-btn" className="bg-green-lt/40 rounded-xl px-6 py-5 font-medium text-green hover:bg-green-lt transition-colors duration-200">
                      Yes
                    </button>
                    <button onClick={() => answer(false)} data-testid="health-no-btn" className="bg-white rounded-xl px-6 py-5 font-medium text-ink-mid hover:bg-paper transition-colors duration-200">
                      No / Not sure
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </FadeUp>
        ) : (
          <FadeUp>
            <div className="bg-white rounded-2xl p-8 lg:p-12 mb-8" data-testid="health-result">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-soft mb-4">Your score</p>
              <div className="flex items-end gap-4 mb-4">
                <span className="font-serif text-7xl text-ink leading-none" data-testid="health-score">{score}</span>
                <span className="font-serif text-3xl text-ink-soft leading-none mb-1">/ 10</span>
                <span className={`ml-auto font-mono text-sm uppercase tracking-[0.2em] ${g.color}`} data-testid="health-grade">{g.label}</span>
              </div>
              <div className="h-2 bg-green-lt rounded-full overflow-hidden mb-6">
                <motion.div className="h-full bg-green rounded-full" initial={{ width: 0 }} animate={{ width: `${score * 10}%` }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} />
              </div>
              <p className="text-sm text-ink-mid leading-relaxed">{g.note}</p>
            </div>

            <div className="space-y-3 mb-10">
              {HEALTH_QUESTIONS.map((q, i) => (
                <div key={i} className={`rounded-xl p-6 ${answers[i] ? "bg-green-lt/30" : "bg-white"}`} data-testid={`health-item-${i}`}>
                  <div className="flex items-start gap-3">
                    {answers[i] ? <CheckCircle2 size={17} className="text-green mt-1 flex-none" /> : <XCircle size={17} className="text-amber mt-1 flex-none" />}
                    <div>
                      <p className="text-sm font-medium text-ink mb-1">{q.q}</p>
                      {!answers[i] && <p className="text-xs text-ink-mid leading-relaxed"><span className="font-mono uppercase tracking-wider text-amber">Fix first: </span>{q.fix}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="health-cta-btn" className="inline-flex items-center gap-2 bg-green text-white text-sm font-medium px-7 py-4 rounded-sm hover:bg-green-mid transition-colors duration-200">
                Fix the gaps with us <ArrowUpRight size={14} />
              </a>
              <button onClick={reset} data-testid="health-retake-btn" className="inline-flex items-center gap-2 border border-rule text-ink text-sm font-medium px-7 py-4 rounded-sm hover:bg-green-lt transition-colors duration-200">
                <RotateCcw size={14} /> Retake
              </button>
            </div>
          </FadeUp>
        )}
      </div>
    </main>
  );
}
